import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  completeOnboarding,
  skipOnboarding,
  setOnboardingStep,
  startOnboarding,
  ONBOARDING_STEPS,
} from '../../store/slices/uiSlice';
import {
  selectTherapistProfile,
  selectClinicId,
  selectTherapistId,
  selectIsNutritionClinic,
} from '../../store/slices/authSlice';
import { useGetPatientsQuery } from '../../store/services/patientsApi';
import { useGetAppointmentsQuery } from '../../store/services/calendarApi';
import { useUpdateTherapistMutation } from '../../store/services/therapistApi';
import OnboardingWelcomeModal from './OnboardingWelcomeModal';
import OnboardingCompleteModal from './OnboardingCompleteModal';
import {
  CaretLeft,
  CaretRight,
  ChartBar,
  UsersThree,
  Lightning,
  Target,
  CalendarCheck,
  CurrencyCircleDollar,
  Toolbox,
  Bell,
  UserPlus,
  NotePencil,
  Check,
  Confetti,
  Play,
  HandPointing,
  ArrowRight,
  Sparkle,
  Clock,
  Receipt,
  ListChecks,
  MagnifyingGlass,
  Plus,
  House,
  GameController,
  User,
  Brain,
  Stop,
  FloppyDisk,
  Envelope,
  FileText,
} from '@phosphor-icons/react';
import LoaderLoop from "../shared/loader/LoaderLoop";
import './OnboardingTour.css';

// LocalStorage keys
const ONBOARDING_STATUS_KEY = 'clinikit_onboarding_status';
const ONBOARDING_PHASE_KEY = 'clinikit_onboarding_phase';
const FORCE_ONBOARDING_KEY = 'clinikit_force_onboarding';

// Get saved onboarding status
const getSavedOnboardingStatus = (therapistId) => {
  try {
    const stored = localStorage.getItem(`${ONBOARDING_STATUS_KEY}_${therapistId}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors
  }
  return null;
};

// Check if onboarding was forced (user clicked "Start Tour")
const isOnboardingForced = (therapistId) => {
  try {
    return localStorage.getItem(`${FORCE_ONBOARDING_KEY}_${therapistId}`) === 'true';
  } catch {
    return false;
  }
};

// Clear force onboarding flag
const clearForceOnboarding = (therapistId) => {
  try {
    localStorage.removeItem(`${FORCE_ONBOARDING_KEY}_${therapistId}`);
  } catch {
    // Ignore errors
  }
};

// Save onboarding status
const saveOnboardingStatus = (therapistId, status) => {
  try {
    localStorage.setItem(
      `${ONBOARDING_STATUS_KEY}_${therapistId}`,
      JSON.stringify({
        ...status,
        updatedAt: new Date().toISOString(),
      })
    );
  } catch {
    // Ignore errors
  }
};

// Get saved phase
const getSavedPhase = (therapistId) => {
  try {
    const stored = localStorage.getItem(`${ONBOARDING_PHASE_KEY}_${therapistId}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors
  }
  return { phaseIndex: 0, stepIndex: 0 };
};

// Save phase
const savePhase = (therapistId, phaseIndex, stepIndex) => {
  try {
    localStorage.setItem(
      `${ONBOARDING_PHASE_KEY}_${therapistId}`,
      JSON.stringify({ phaseIndex, stepIndex })
    );
  } catch {
    // Ignore errors
  }
};

// Spotlight component that highlights an element
function Spotlight({ target, children, placement = 'bottom', onNext, onPrev, onSkip, stepIndex, totalSteps, isLastStep, showNavigation = true, nextLabel, isActionStep, hideNextButton = false, blockInteraction = false, scrollToTarget = false }) {
  const [position, setPosition] = useState(null);
  const tooltipRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  
  // Scroll to target element if scrollToTarget is true
  useEffect(() => {
    if (!scrollToTarget) return;
    
    const element = document.querySelector(target);
    if (element) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [target, scrollToTarget]);
  
  useEffect(() => {
    const updatePosition = () => {
      const element = document.querySelector(target);
      if (!element) {
        setPosition(null);
        return;
      }
      
      const rect = element.getBoundingClientRect();
      const padding = 8;
      
      setPosition({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        elementRect: rect,
      });
    };
    
    updatePosition();
    
    // Update on resize and scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    
    // Also observe for DOM changes
    const observer = new MutationObserver(updatePosition);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      observer.disconnect();
    };
  }, [target]);
  
  // Calculate tooltip position after render - ensure always visible
  useEffect(() => {
    if (!tooltipRef.current) return;
    
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 20;
    
    let top, left;
    
    // If no target found, center the tooltip
    if (!position) {
      top = (viewportHeight - tooltipRect.height) / 2;
      left = (viewportWidth - tooltipRect.width) / 2;
      setTooltipPos({ top, left });
      return;
    }
    
    // Calculate available space in each direction
    const spaceTop = position.top;
    const spaceBottom = viewportHeight - (position.top + position.height);
    const spaceLeft = position.left;
    const spaceRight = viewportWidth - (position.left + position.width);
    
    // Determine best placement based on available space
    let actualPlacement = placement;
    
    // Check if requested placement has enough space
    const needsHeight = tooltipRect.height + margin;
    const needsWidth = tooltipRect.width + margin;
    
    if (placement === 'bottom' && spaceBottom < needsHeight) {
      actualPlacement = spaceTop > spaceBottom ? 'top' : 'bottom';
    } else if (placement === 'top' && spaceTop < needsHeight) {
      actualPlacement = spaceBottom > spaceTop ? 'bottom' : 'top';
    } else if (placement === 'left' && spaceLeft < needsWidth) {
      actualPlacement = spaceRight > spaceLeft ? 'right' : 'left';
    } else if (placement === 'right' && spaceRight < needsWidth) {
      actualPlacement = spaceLeft > spaceRight ? 'left' : 'right';
    }
    
    // Calculate position based on actual placement
    switch (actualPlacement) {
      case 'top':
        top = position.top - tooltipRect.height - margin;
        left = position.left + (position.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = position.top + position.height + margin;
        left = position.left + (position.width - tooltipRect.width) / 2;
        break;
      case 'bottom-left':
        top = viewportHeight - tooltipRect.height - margin;
        left = margin;
        break;
      case 'bottom-right':
        top = viewportHeight - tooltipRect.height - margin;
        left = viewportWidth - tooltipRect.width - margin;
        break;
      case 'left':
        top = position.top + (position.height - tooltipRect.height) / 2;
        left = position.left - tooltipRect.width - margin;
        break;
      case 'right':
        top = position.top + (position.height - tooltipRect.height) / 2;
        left = position.left + position.width + margin;
        break;
      default:
        top = position.top + position.height + margin;
        left = position.left + (position.width - tooltipRect.width) / 2;
    }
    
    // Final boundary checks - ensure tooltip stays within viewport
    // Left boundary
    if (left < margin) {
      left = margin;
    }
    // Right boundary
    if (left + tooltipRect.width > viewportWidth - margin) {
      left = viewportWidth - tooltipRect.width - margin;
    }
    // Top boundary
    if (top < margin) {
      top = margin;
    }
    // Bottom boundary
    if (top + tooltipRect.height > viewportHeight - margin) {
      top = viewportHeight - tooltipRect.height - margin;
    }
    
    setTooltipPos({ top, left });
  }, [position, placement]);
  
  const progress = ((stepIndex + 1) / totalSteps) * 100;
  
  // Fallback position for center of screen when element not found
  const fallbackPos = {
    top: window.innerHeight / 2 - 100,
    left: window.innerWidth / 2 - 200,
    width: 400,
    height: 200,
  };
  
  const spotlightPos = position || fallbackPos;
  const hasTarget = !!position;
  
  // Calculate dimensions for the 4 overlay panels around the cutout
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  return createPortal(
    <div className="onboarding-spotlight-overlay">
      {/* Dark overlay using 4 panels around the cutout - allows clicking through */}
      {hasTarget ? (
        <>
          {/* Top panel */}
          <div 
            className="onboarding-overlay-panel"
            style={{
              top: 0,
              left: 0,
              width: '100%',
              height: Math.max(0, spotlightPos.top),
            }}
          />
          {/* Bottom panel */}
          <div 
            className="onboarding-overlay-panel"
            style={{
              top: spotlightPos.top + spotlightPos.height,
              left: 0,
              width: '100%',
              height: Math.max(0, viewportHeight - spotlightPos.top - spotlightPos.height),
            }}
          />
          {/* Left panel */}
          <div 
            className="onboarding-overlay-panel"
            style={{
              top: spotlightPos.top,
              left: 0,
              width: Math.max(0, spotlightPos.left),
              height: spotlightPos.height,
            }}
          />
          {/* Right panel */}
          <div 
            className="onboarding-overlay-panel"
            style={{
              top: spotlightPos.top,
              left: spotlightPos.left + spotlightPos.width,
              width: Math.max(0, viewportWidth - spotlightPos.left - spotlightPos.width),
              height: spotlightPos.height,
            }}
          />
          {/* Block interaction panel - covers the cutout when blockInteraction is true */}
          {blockInteraction && (
            <div 
              className="onboarding-overlay-panel onboarding-block-interaction"
              style={{
                top: spotlightPos.top,
                left: spotlightPos.left,
                width: spotlightPos.width,
                height: spotlightPos.height,
                background: 'transparent',
                cursor: 'not-allowed',
              }}
            />
          )}
        </>
      ) : (
        /* Full overlay when no target */
        <div 
          className="onboarding-overlay-panel onboarding-overlay-full"
          style={{
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      )}
      
      {/* Spotlight border glow - only show when target exists */}
      {hasTarget && (
        <div 
          className="onboarding-spotlight-border"
          style={{
            top: spotlightPos.top,
            left: spotlightPos.left,
            width: spotlightPos.width,
            height: spotlightPos.height,
          }}
        />
      )}
      
      {/* Tooltip */}
      <div 
        ref={tooltipRef}
        className="onboarding-tooltip"
        style={tooltipPos}
      >
        {/* Progress bar */}
        <div className="onboarding-progress">
          <div 
            className="onboarding-progress-bar" 
            style={{ width: `${progress}%` }} 
          />
        </div>
        
        {/* Step indicator */}
        <div className="onboarding-step-indicator">
          שלב {stepIndex + 1} מתוך {totalSteps}
        </div>
        
        {/* Content */}
        <div className="onboarding-content">
          {children}
        </div>
        
        {/* Actions */}
        {showNavigation && (
          <div className="onboarding-actions">
            
            <div className="onboarding-actions-right">
              <button className="onboarding-btn onboarding-btn-skip" onClick={onSkip}>
                דלג על ההדרכה
              </button>
              
              {!hideNextButton && (
                <button 
                  className={`onboarding-btn onboarding-btn-primary ${isActionStep ? 'action-step' : ''}`} 
                  onClick={onNext}
                >
                  {isLastStep ? (
                    <>
                      <Confetti size={18} weight="fill" />
                      סיימתי!
                    </>
                  ) : isActionStep ? (
                    <>
                      <HandPointing size={18} weight="fill" />
                      {nextLabel || 'לחץ להמשך'}
                    </>
                  ) : (
                    <>
                      <CaretLeft size={18} weight="bold" />
                      {nextLabel || 'המשך'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Define the complete onboarding flow - fully interactive, no "continue" buttons
const ONBOARDING_FLOW = [
  // Phase 0: Navigate to Patients (first real step)
  {
    path: '/dashboard',
    pathMatch: 'exact',
    steps: [
      {
        target: '.nav-item[href="/dashboard/patients"]',
        icon: UsersThree,
        title: 'שלב 1: המטופלים שלך',
        content: (
          <div>
            <p><strong>יאללה בואו נלמד את המערכת!</strong></p>
            <p className="onboarding-fun">מבטיח שזה יהיה קליל ולא חופר מידי 😉</p>
            <p>נתחיל מהלב של הקליניקה - <strong>המטופלים</strong>.</p>
            <p style={{ marginTop: '12px' }}>בעמוד הזה תוכל:</p>
            <ul className="onboarding-list">
              <li><UsersThree size={14} weight="regular" className="list-icon" /> לראות את כל המטופלים שלך</li>
              <li><MagnifyingGlass size={14} weight="regular" className="list-icon" /> לחפש ולסנן מטופלים</li>
              <li><User size={14} weight="regular" className="list-icon" /> להיכנס ל"כרטיס לקוח" ולנהל את תיק המטופל</li>
            </ul>
            <p className="onboarding-hint" style={{ fontSize: '12px', marginTop: '8px' }}>
              כולל היסטוריית טיפולים, קבצים משויכים, טפסים, שאלונים ומעקב פיננסים לכל מטופל.
            </p>
            <p className="onboarding-action">
              <HandPointing size={16} weight="fill" className="action-icon" />
              <strong>לחץ על "המטופלים שלי"</strong> בתפריט
            </p>
          </div>
        ),
        placement: 'left',
        hideNextButton: true,
        navigateTo: '/dashboard/patients',
      },
    ],
  },
  // Phase 1: Patients Page - Click Add Patient
  {
    path: '/dashboard/patients',
    pathMatch: 'exact',
    steps: [
      {
        target: '.add-patient-btn',
        icon: UserPlus,
        title: 'שלב 2: יצירת מטופל ראשון',
        content: (
          <div>
            <p>מעולה! הגעת לעמוד המטופלים.</p>
            <p>כאן תראה את כל המטופלים שלך ברשימה מסודרת.</p>
            <p>כרגע הרשימה ריקה - בוא ניצור את המטופל הראשון שלך!</p>
            <p className="onboarding-hint">כל מה שצריך זה שם וטלפון - והמערכת תעשה את השאר.</p>
            <p className="onboarding-action">
              <HandPointing size={16} weight="fill" className="action-icon" />
              <strong>לחץ על "+ מטופל חדש"</strong>
            </p>
          </div>
        ),
        placement: 'bottom',
        hideNextButton: true,
        navigateTo: '/dashboard/patients/new',
      },
    ],
  },
  // Phase 2: Create Patient Page - Fill form and save
  {
    path: '/dashboard/patients/new',
    pathMatch: 'exact',
    steps: [
      {
        target: '.create-patient-page .form-container',
        icon: NotePencil,
        title: 'שלב 3: יצירת מטופל',
        content: (
          <div>
            <p><strong>מצוין! זה טופס יצירת מטופל חדש.</strong></p>
            <p style={{ marginTop: '10px' }}>המידע המינימלי הנדרש הוא רק <strong>שם משפחה</strong> ו<strong>מספר טלפון</strong>.</p>
            <p className="onboarding-hint" style={{ marginTop: '10px', fontSize: '12px' }}>
              <strong>רוצה להוסיף עוד מידע?</strong> עבור בין הלשוניות למעלה (פרטים נוספים, רקע רפואי, ניהול ולו"ז, תיק מטופל) — או שמור כבר עכשיו והשלם אחר כך.
            </p>
            <p className="onboarding-fun" style={{ marginTop: '8px', fontSize: '12px' }}>
              ככל שתוסיף יותר מידע - יהיה לך יותר קל לנהל את המטופל, וגם ה-AI יוכל לתת סיכומים והמלצות מדויקים יותר!
            </p>
            <p className="onboarding-hint" style={{ color: '#e67e22', fontSize: '12px', marginTop: '12px', padding: '8px', background: 'rgba(230, 126, 34, 0.1)', borderRadius: '6px' }}>
              💡 לצורך ההדרכה אנחנו ממליצים לרשום את הפרטים שלכם בתור מטופלים.
            </p>
            <p className="onboarding-action" style={{ marginTop: '12px' }}>
              <Sparkle size={16} weight="fill" className="action-icon" />
              מלא שם משפחה וטלפון, ולחץ על <strong>"שמור מטופל"</strong>
            </p>
          </div>
        ),
        placement: 'bottom-right',
        hideNextButton: true,
        waitForAction: 'patient_created',
      },
    ],
  },
  // Phase 3: Go to Calendar
  {
    path: '/dashboard',
    pathMatch: 'exact',
    steps: [
      {
        target: '.nav-item[href="/dashboard/calendar"]',
        icon: CalendarCheck,
        title: 'שלב 4: היומן שלך',
        content: (
          <div>
            <p><strong>מצוין! יצרת את המטופל הראשון שלך!</strong></p>
            <p>עכשיו בוא נלמד על היומן - הלב הפועם של הקליניקה.</p>
            <p>ביומן תוכל:</p>
            <ul className="onboarding-list">
              <li><CalendarCheck size={14} weight="regular" className="list-icon" /> לקבוע פגישות למטופלים</li>
              <li><Clock size={14} weight="regular" className="list-icon" /> לראות את לוח הזמנים שלך</li>
              <li><Bell size={14} weight="regular" className="list-icon" /> לשלוח תזכורות אוטומטיות</li>
            </ul>
            <p className="onboarding-action">
              <HandPointing size={16} weight="fill" className="action-icon" />
              <strong>לחץ על "יומן ותורים"</strong> בתפריט
            </p>
          </div>
        ),
        placement: 'left',
        hideNextButton: true,
        navigateTo: '/dashboard/calendar',
      },
    ],
  },
  // Phase 4: Calendar Page - Open Appointment Modal
  {
    path: '/dashboard/calendar',
    pathMatch: 'exact',
    steps: [
      {
        target: '.calendar-page',
        icon: CalendarCheck,
        title: 'שלב 5: יצירת פגישה ראשונה',
        content: (
          <div>
            <p><strong>ברוכים הבאים ליומן!</strong></p>
            <p>כאן תקבע את כל הפגישות שלך. עכשיו ניצור את הפגישה הראשונה!</p>
            <p><strong>איך ליצור פגישה:</strong></p>
            <ul className="onboarding-list">
              <li><HandPointing size={14} weight="fill" className="list-icon" /> <strong>לחץ על תא ריק</strong> ביומן</li>
              <li><Plus size={14} weight="bold" className="list-icon" /> <strong>או</strong> לחץ על כפתור "פגישה חדשה" למעלה</li>
            </ul>
            <p className="onboarding-action">
              <Sparkle size={16} weight="fill" className="action-icon" />
              <strong>לחץ על תא ביומן</strong> או על <strong>"פגישה חדשה"</strong>
            </p>
          </div>
        ),
        placement: 'right',
        hideNextButton: true,
        waitForAction: 'appointment_modal_opened',
      },
    ],
  },
  // Phase 5: Appointment Modal - Fill Details
  {
    path: '/dashboard/calendar',
    pathMatch: 'exact',
    steps: [
      {
        target: '.appointment-modal',
        icon: CalendarCheck,
        title: 'שלב 6: יצירת פגישה',
        content: (
          <div>
            <p><strong>מעולה! נפתח חלון יצירת פגישה.</strong></p>
            
            <p style={{ marginTop: '10px', marginBottom: '6px' }}><strong>הפופאפ כולל:</strong></p>
            <ul className="onboarding-list" style={{ fontSize: '12px' }}>
              <li><User size={14} weight="fill" className="list-icon" /> <strong>פרטי המטופל</strong> - בחר מטופל מהרשימה</li>
              <li><Clock size={14} weight="fill" className="list-icon" /> <strong>מועד הפגישה</strong> - תאריך, שעה ומשך</li>
              <li><NotePencil size={14} weight="fill" className="list-icon" /> <strong>פרטי טיפול</strong> - סוג טיפול ותעריף (אופציונלי)</li>
              <li><FileText size={14} weight="fill" className="list-icon" /> <strong>הערות</strong> - לשימוש פנימי שלך</li>
            </ul>
            
            <p className="onboarding-hint" style={{ fontSize: '11px', marginTop: '10px' }}>
              <strong>פיצ'רים נוספים (לא פעילים בהדרכה):</strong><br/>
              • <strong>סדרת מפגשים</strong> - ליצירת סדרה של מפגשים קבועים<br/>
              • <strong>שליחת SMS</strong> - תזכורת אוטומטית למטופל
            </p>
            
            <p className="onboarding-hint" style={{ color: '#e67e22', fontSize: '11px', marginTop: '8px', padding: '6px 8px', background: 'rgba(230, 126, 34, 0.1)', borderRadius: '6px' }}>
              💡 התאריך והשעה הוגדרו אוטומטית לעכשיו - כדי שתוכל לחוות פגישת לייב!
            </p>
            
            <p className="onboarding-action" style={{ marginTop: '10px' }}>
              <Sparkle size={16} weight="fill" className="action-icon" />
              בחר מטופל ולחץ <strong>"צור פגישה"</strong>
            </p>
          </div>
        ),
        placement: 'bottom-right',
        hideNextButton: true,
        waitForAction: 'appointment_created',
      },
    ],
  },
  // Phase 6: Calendar - Show Ongoing Appointment Banner (bottom banner)
  {
    path: '/dashboard/calendar',
    pathMatch: 'exact',
    steps: [
      {
        target: '.mobile-live-session-banner',
        icon: Play,
        title: 'שלב 7: פגישה פעילה!',
        content: (
          <div>
            <p><strong>מעולה! הפגישה נוצרה והיא פעילה עכשיו!</strong></p>
            <p>רואה את הפס הכתום בתחתית המסך? זה אומר שיש פגישה פעילה.</p>
            <p><strong>מה זה פגישת לייב?</strong></p>
            <p>אם תרצה לתעד את הטיפול בזמן אמת, לרשום הערות מהירות, או להעזר בכלי AI במהלך הפגישה - זה המקום!</p>
            <p className="onboarding-hint">חשוב לדעת: פגישת לייב היא <strong>לא חובה</strong>! אפשר לעבוד בלעדיה והכל יתנהל כרגיל.</p>
            <p className="onboarding-action">
              <HandPointing size={16} weight="fill" className="action-icon" />
              <strong>לחץ על הפס הכתום</strong> כדי להיכנס לפגישת לייב
            </p>
          </div>
        ),
        placement: 'top',
        hideNextButton: true,
        waitForAction: 'live_session_opened',
      },
    ],
  },
  // Phase 7: Live Session - Overview
  {
    path: '/dashboard/live-session',
    pathMatch: 'startsWith',
    steps: [
      {
        target: '.live-session-page',
        icon: Play,
        title: 'שלב 8: פגישה בלייב!',
        content: (
          <div>
            <p><strong>וואו! הנה מסך הפגישה בלייב!</strong></p>
            <p>זה המסך שתראה בזמן אמת כשמטופל נמצא אצלך בקליניקה.</p>
            <p><strong>מה יש כאן:</strong></p>
            <ul className="onboarding-list">
              <li><Clock size={14} weight="regular" className="list-icon" /> <strong>שעון וטיימר</strong> - מעקב אחרי זמן הפגישה</li>
              <li><Sparkle size={14} weight="fill" className="list-icon" /> <strong>כלי AI</strong> - עזרה חכמה בזמן אמת</li>
              <li><NotePencil size={14} weight="regular" className="list-icon" /> <strong>תיעוד</strong> - רישום שהופך לסיכום</li>
            </ul>
            <p className="onboarding-fun">בוא נכיר את הכלים המיוחדים!</p>
            <p className="onboarding-action">
              <HandPointing size={16} weight="fill" className="action-icon" />
              גלול למטה לראות את <strong>כלי ה-AI</strong>
            </p>
          </div>
        ),
        placement: 'left',
        hideNextButton: false,
        nextLabel: 'הבנתי, המשך',
        blockInteraction: true,
      },
    ],
  },
  // Phase 8: Live Session - AI Tools
  {
    path: '/dashboard/live-session',
    pathMatch: 'startsWith',
    steps: [
      {
        target: '.tools-section',
        icon: Sparkle,
        title: 'שלב 9: כלי AI חכמים',
        content: (
          <div>
            <p><strong>זה החלק הכי מגניב!</strong></p>
            <p>כאן יש לך גישה לכלי AI שיעזרו לך בזמן הפגישה:</p>
            <ul className="onboarding-list">
              <li><User size={14} weight="duotone" className="list-icon" /> <strong>התייעצות מטופל</strong> - שאל שאלות על המטופל</li>
              <li><Brain size={14} weight="duotone" className="list-icon" /> <strong>התייעצות טיפול</strong> - קבל תובנות על הטיפול</li>
            </ul>
            <p className="onboarding-hint">הכלים האלה לומדים את המטופלים שלך ונותנים המלצות מותאמות אישית!</p>
            <p className="onboarding-action">
              <HandPointing size={16} weight="fill" className="action-icon" />
              גלול למטה לראות את <strong>הערות הלייב</strong>
            </p>
          </div>
        ),
        placement: 'left',
        hideNextButton: false,
        nextLabel: 'הבנתי, המשך',
        blockInteraction: true,
        scrollToTarget: true,
        optional: true,
      },
    ],
  },
  // Phase 9: Live Session - Notes Section
  {
    path: '/dashboard/live-session',
    pathMatch: 'startsWith',
    steps: [
      {
        target: '.notes-section',
        icon: Lightning,
        title: 'שלב 10: הערות בזמן אמת',
        content: (
          <div>
            <p><strong>הערות בלייב - הדרך הכי מהירה לתעד!</strong></p>
            <p>בזמן הפגישה אתה יכול:</p>
            <ul className="onboarding-list">
              <li><Plus size={14} weight="bold" className="list-icon" /> <strong>להוסיף הערות מהירות</strong> - רעיונות, תובנות, משימות</li>
              <li><NotePencil size={14} weight="regular" className="list-icon" /> <strong>לכתוב תיעוד</strong> - שיהפוך אוטומטית לסיכום טיפול</li>
              <li><FloppyDisk size={14} weight="regular" className="list-icon" /> <strong>הכל נשמר אוטומטית</strong> - אין צורך ללחוץ שמור</li>
            </ul>
            <p className="onboarding-fun">כל מה שתכתוב כאן יהפוך לסיכום טיפול מסודר!</p>
            <p className="onboarding-action">
              <HandPointing size={16} weight="fill" className="action-icon" />
              עכשיו נלמד על <strong>סיום הפגישה</strong>
            </p>
          </div>
        ),
        placement: 'top',
        hideNextButton: false,
        nextLabel: 'הבנתי, המשך',
        blockInteraction: true,
        scrollToTarget: true,
        optional: true,
      },
    ],
  },
  // Phase 10: Live Session - End Session Button
  {
    path: '/dashboard/live-session',
    pathMatch: 'startsWith',
    steps: [
      {
        target: '.end-session-btn',
        icon: Stop,
        title: 'שלב 11: סיום פגישה',
        content: (
          <div>
            <p><strong>כשהפגישה מסתיימת - לחץ כאן!</strong></p>
            <p>כפתור "סיום פגישה" עושה כמה דברים:</p>
            <ul className="onboarding-list">
              <li><Check size={14} weight="bold" className="list-icon" /> שומר את כל ההערות והתיעוד</li>
              <li><Check size={14} weight="bold" className="list-icon" /> מעביר את הפגישה ל"ממתין לטיפול"</li>
              <li><Check size={14} weight="bold" className="list-icon" /> מאפשר להשלים תשלום וסיכום</li>
            </ul>
            <p className="onboarding-action">
              <HandPointing size={16} weight="fill" className="action-icon" />
              <strong>לחץ על "סיום פגישה"</strong>
            </p>
          </div>
        ),
        placement: 'bottom',
        hideNextButton: true,
        waitForAction: 'end_session_modal_opened',
      },
    ],
  },
  // Phase 11: Live Session - Confirm End Session
  {
    path: '/dashboard/live-session',
    pathMatch: 'startsWith',
    steps: [
      {
        target: '.end-session-modal',
        icon: Check,
        title: 'שלב 12: אישור סיום',
        content: (
          <div>
            <p><strong>אישור סיום הפגישה</strong></p>
            <p>זהו! עכשיו נסיים את הפגישה ונעבור לראות מה קורה אחרי שפגישה מסתיימת.</p>
            <p className="onboarding-hint">הפגישה תעבור ל"ממתין לטיפול" - שם תוכל לאשר הגעה, לגבות תשלום ולכתוב סיכום.</p>
            <p className="onboarding-action">
              <HandPointing size={16} weight="fill" className="action-icon" />
              <strong>לחץ על "סיים סשן" בחלון</strong>
            </p>
          </div>
        ),
        placement: 'left',
        hideNextButton: true,
        waitForAction: 'session_ended',
      },
    ],
  },
  // Phase 12: Dashboard - Collapsible Sections Overview (TABS BAR)
  {
    path: '/dashboard',
    pathMatch: 'exact',
    steps: [
      {
        target: '.cs-tabs-wrapper',
        icon: Target,
        title: 'שלב 13: מרכז הניהול שלך',
        // Function form: the pending-actions bullet differs per clinic type.
        content: ({ isNutritionClinic }) => (
          <div>
            <p><strong>מצוין! הפגישה הסתיימה!</strong></p>
            <p>הנה מרכז הניהול החדש שלך - כאן תמצא את כל מה שצריך במקום אחד:</p>
            
            <ul className="onboarding-list">
              <li><Lightning size={14} weight="fill" className="list-icon" style={{color: '#f87171'}} /> <strong>פעולות ממתינות</strong> - {isNutritionClinic
                ? 'דוחות אכילה שנסגרו וממתינים לסקירה, ולקוחות שמועד המדידה שלהם הגיע.'
                : 'פגישות שהסתיימו ומחכות לאישור ועדכוני סטטוסים לגבי הפגישה - אני אסביר על זה בפירוט בשלב הבא.'}</li>
              <li><Bell size={14} weight="fill" className="list-icon" style={{color: '#f59e0b'}} /> <strong>התראות</strong> - כל ההתראות החשובות שלך (תצוגה בלבד)</li>
              <li><CalendarCheck size={14} weight="fill" className="list-icon" style={{color: '#4dd9b4'}} /> <strong>יומן</strong> - הפגישות של היום והשבוע הקרוב (תצוגה בלבד)</li>
              <li><ListChecks size={14} weight="fill" className="list-icon" style={{color: '#3b82f6'}} /> <strong>משימות</strong> - רשימת המשימות שלך (תצוגה בלבד)</li>
              <li><Bell size={14} weight="fill" className="list-icon" style={{color: '#8b5cf6'}} /> <strong>תזכורות</strong> - תזכורות שהגדרת (תצוגה בלבד)</li>
              <li><UsersThree size={14} weight="fill" className="list-icon" style={{color: '#ec4899'}} /> <strong>מתעניינים</strong> - פניות חדשות מהאתר (תצוגה בלבד)</li>
            </ul>
            
            <p className="onboarding-hint">
              <strong>טיפ:</strong> לחיצה על כל טאב תפתח אותו לרוחב עם כל הפרטים. המספר על כל אייקון מראה כמה פריטים יש בכל קטגוריה.
            </p>
          </div>
        ),
        placement: 'top',
        hideNextButton: false,
        nextLabel: 'מעולה, המשך',
        blockInteraction: true,
      },
    ],
  },
  // Phase 13: Dashboard - Pending Actions Tab (detailed)
  // Teaches the 3-stage appointment update. A nutrition clinic replaces that
  // queue wholesale (NutritionPendingSection), so the phase is dropped from
  // its flow — see buildTourFlow. The previous step then takes over the
  // navigateTo below.
  {
    path: '/dashboard',
    pathMatch: 'exact',
    nutritionHidden: true,
    steps: [
      {
        target: '[data-section-id="pendingActions"]',
        icon: Lightning,
        title: 'שלב 14: פעולות ממתינות',
        content: (
          <div>
            <p><strong>הנה הפגישה שלך ממתינה לטיפול!</strong></p>
            <p>כל פגישה שמסתיימת מגיעה לכאן כ"כרטיס" בקרוסלה, עם 3 שלבים להשלמה:</p>
            
            <ul className="onboarding-list">
              <li><Clock size={14} weight="fill" className="list-icon" style={{color: '#14b8a6'}} /> <strong>אישור</strong> - האם הפגישה התרחשה? אם כן - תלחץ ✓, אם לא - תלחץ ✕ והיא תתועד כביטול.</li>
              <li><NotePencil size={14} weight="fill" className="list-icon" style={{color: '#14b8a6'}} /> <strong>סיכום</strong> - האם לכתוב סיכום טיפול? אם כן - תועבר לדף הסיכום. אם לא עכשיו - תוכל להוסיף תזכורת.</li>
              <li><CurrencyCircleDollar size={14} weight="fill" className="list-icon" style={{color: '#14b8a6'}} /> <strong>תשלום</strong> - האם התשלום טופל? תוכל לרשום סכום, אמצעי תשלום, ואפילו להפיק חשבונית.</li>
            </ul>
            
            <p className="onboarding-hint">
              <strong>איך זה עובד:</strong> כל שלב נצבע בירוק כשהוא מושלם. כשכל 3 השלבים ירוקים - הכרטיס נעלם והפגישה מתועדת בהיסטוריה!
            </p>
            
            <p className="onboarding-fun">
              <strong>טיפ:</strong> אם יש הרבה פגישות, תוכל לגלול בקרוסלה. פס הגלילה האדום למטה מראה שיש עוד כרטיסים.
            </p>
            
            <p>אין צורך לבצע כאן פעולות עכשיו - בהמשך תוכל לחזור ולהשלים.</p>
          </div>
        ),
        placement: 'top',
        hideNextButton: false,
        nextLabel: 'אני מבין, המשך',
        blockInteraction: true,
        navigateTo: '/dashboard/tasks',
      },
    ],
  },
  // Phase 14: Toolbox Page - Overview (יש navigateTo בשלב הקודם)
  {
    path: '/dashboard/tasks',
    pathMatch: 'exact',
    steps: [
      {
        target: '.toolbox-page',
        icon: Toolbox,
        title: 'שלב 15: ברוכים הבאים לארגז הכלים!',
        content: (
          <div>
            <p><strong>זה המקום שבו מנהלים את כל הדברים החשובים:</strong></p>
            <p>כאן תמצא 6 כלים שיעזרו לך לנהל את הקליניקה בצורה יעילה.</p>
            <p className="onboarding-hint">בואו נעבור על כל אחד מהם!</p>
          </div>
        ),
        placement: 'right',
        hideNextButton: false,
        nextLabel: 'בוא נתחיל',
        blockInteraction: true,
        optional: true,
      },
    ],
  },
  // Phase 15: Toolbox - Tasks Tab Explanation
  {
    path: '/dashboard/tasks',
    pathMatch: 'exact',
    steps: [
      {
        target: '.toolbox-tabs',
        icon: ListChecks,
        title: 'שלב 16: משימות',
        content: (
          <div>
            <p><strong>משימות - הרשימה שלך לעשות!</strong></p>
            <ul className="onboarding-list">
              <li><Check size={14} weight="bold" className="list-icon" /> יצירת משימות אישיות וקליניקתיות</li>
              <li><Clock size={14} weight="regular" className="list-icon" /> הגדרת תאריך יעד ותזכורת</li>
              <li><UsersThree size={14} weight="regular" className="list-icon" /> שיתוף משימות עם צוות הקליניקה</li>
              <li><Target size={14} weight="regular" className="list-icon" /> מעקב אחר התקדמות</li>
            </ul>
            <p className="onboarding-fun">תגיד שלום לפתקים שנעלמים!</p>
          </div>
        ),
        placement: 'bottom',
        hideNextButton: false,
        nextLabel: 'המשך לפתקים',
        performAction: 'click_reminders_tab',
        optional: true,
      },
    ],
  },
  // Phase 16: Toolbox - Reminders Tab Explanation
  {
    path: '/dashboard/tasks',
    pathMatch: 'exact',
    steps: [
      {
        target: '.toolbox-tabs',
        icon: Bell,
        title: 'שלב 17: פתקים',
        content: (
          <div>
            <p><strong>פתקים - שלא תשכח כלום!</strong></p>
            <ul className="onboarding-list">
              <li><Bell size={14} weight="fill" className="list-icon" /> יצירת פתקים לעצמך</li>
              <li><Clock size={14} weight="regular" className="list-icon" /> קביעת זמן התראה</li>
              <li><Envelope size={14} weight="regular" className="list-icon" /> קבלת התראה במייל או בהודעה</li>
            </ul>
            <p className="onboarding-hint">טיפ: אפשר ליצור פתקים חוזרים!</p>
          </div>
        ),
        placement: 'bottom',
        hideNextButton: false,
        nextLabel: 'המשך למתעניינים',
        performAction: 'click_leads_tab',
        optional: true,
      },
    ],
  },
  // Phase 17: Toolbox - Leads Tab Explanation
  {
    path: '/dashboard/tasks',
    pathMatch: 'exact',
    steps: [
      {
        target: '.toolbox-tabs',
        icon: UsersThree,
        title: 'שלב 18: מתעניינים',
        content: (
          <div>
            <p><strong>מתעניינים - פניות חדשות!</strong></p>
            <ul className="onboarding-list">
              <li><Envelope size={14} weight="regular" className="list-icon" /> כל הפניות מטופס האתר</li>
              <li><User size={14} weight="regular" className="list-icon" /> המרה מהירה למטופל חדש</li>
              <li><Clock size={14} weight="regular" className="list-icon" /> מעקב אחרי סטטוס המתעניין</li>
            </ul>
            <p className="onboarding-fun">כל מתעניין = מטופל פוטנציאלי!</p>
          </div>
        ),
        placement: 'bottom',
        hideNextButton: false,
        nextLabel: 'המשך לטפסים',
        performAction: 'click_forms_tab',
        optional: true,
      },
    ],
  },
  // Phase 18: Toolbox - Forms Tab Explanation
  {
    path: '/dashboard/tasks',
    pathMatch: 'exact',
    steps: [
      {
        target: '.toolbox-tabs',
        icon: FileText,
        title: 'שלב 19: טפסים',
        content: (
          <div>
            <p><strong>טפסים - מסמכים דיגיטליים!</strong></p>
            <ul className="onboarding-list">
              <li><NotePencil size={14} weight="regular" className="list-icon" /> יצירת טפסי הסכמה וקבלה</li>
              <li><Envelope size={14} weight="regular" className="list-icon" /> שליחה למטופל לחתימה דיגיטלית</li>
              <li><FloppyDisk size={14} weight="regular" className="list-icon" /> שמירה אוטומטית בתיק המטופל</li>
            </ul>
            <p className="onboarding-hint">טיפ: אפשר ליצור תבניות מותאמות אישית!</p>
          </div>
        ),
        placement: 'bottom',
        hideNextButton: false,
        nextLabel: 'המשך לשאלונים',
        performAction: 'click_questionnaires_tab',
        optional: true,
      },
    ],
  },
  // Phase 19: Toolbox - Questionnaires Tab Explanation
  {
    path: '/dashboard/tasks',
    pathMatch: 'exact',
    steps: [
      {
        target: '.toolbox-tabs',
        icon: ListChecks,
        title: 'שלב 20: שאלונים',
        content: (
          <div>
            <p><strong>שאלונים - מידע מהמטופל מראש!</strong></p>
            <ul className="onboarding-list">
              <li><ListChecks size={14} weight="regular" className="list-icon" /> יצירת שאלונים מותאמים אישית</li>
              <li><Envelope size={14} weight="regular" className="list-icon" /> שליחה למטופל לפני הפגישה</li>
              <li><Check size={14} weight="bold" className="list-icon" /> קבלת תשובות ישירות לתיק המטופל</li>
            </ul>
            <p className="onboarding-hint">טיפ: שאלונים חוסכים זמן יקר בפגישה!</p>
          </div>
        ),
        placement: 'bottom',
        hideNextButton: false,
        nextLabel: 'המשך לתבניות',
        performAction: 'click_templates_tab',
        optional: true,
      },
    ],
  },
  // Phase 20: Toolbox - Templates Tab Explanation
  {
    path: '/dashboard/tasks',
    pathMatch: 'exact',
    steps: [
      {
        target: '.toolbox-tabs',
        icon: FileText,
        title: 'שלב 21: תבניות',
        content: (
          <div>
            <p><strong>תבניות - חסוך זמן בכתיבה!</strong></p>
            <ul className="onboarding-list">
              <li><FileText size={14} weight="regular" className="list-icon" /> יצירת תבניות לסיכומי טיפול</li>
              <li><Clock size={14} weight="regular" className="list-icon" /> שימוש חוזר בטקסטים נפוצים</li>
              <li><Sparkle size={14} weight="fill" className="list-icon" /> התאמה אישית לכל סוג טיפול</li>
            </ul>
            <p className="onboarding-hint">טיפ: תבניות טובות = תיעוד מהיר ועקבי!</p>
          </div>
        ),
        placement: 'bottom',
        hideNextButton: false,
        nextLabel: 'המשך לפיננסים',
        navigateTo: '/dashboard/finance',
        optional: true,
      },
    ],
  },
  // Phase 21: Finance Page - Overview
  {
    path: '/dashboard/finance',
    pathMatch: 'exact',
    steps: [
      {
        target: '.finance-page',
        icon: CurrencyCircleDollar,
        title: 'שלב 22: ניהול כספים',
        content: (
          <div>
            <p><strong>ברוכים הבאים למרכז הכספים!</strong></p>
            <p>כאן תנהל את כל העניינים הכספיים של הקליניקה שלך.</p>
            <p className="onboarding-hint">בואו נעבור על הכלים העיקריים...</p>
          </div>
        ),
        placement: 'right',
        hideNextButton: false,
        nextLabel: 'המשך',
        blockInteraction: true,
        optional: true,
      },
    ],
  },
  // Phase 22: Finance - Kanban Board
  {
    path: '/dashboard/finance',
    pathMatch: 'exact',
    steps: [
      {
        target: '.finance-kanban-section',
        icon: ChartBar,
        title: 'שלב 23: לוח תשלומים',
        content: (
          <div>
            <p><strong>לוח הקנבן - מעקב תשלומים חכם!</strong></p>
            <ul className="onboarding-list">
              <li><Clock size={14} weight="regular" className="list-icon" /> <strong>ממתין לגבייה</strong> - פגישות שהתקיימו ועוד לא שולמו</li>
              <li><Check size={14} weight="bold" className="list-icon" /> <strong>שולם</strong> - היסטוריית תשלומים מלאה</li>
            </ul>
            <p className="onboarding-hint">פשוט גוררים כרטיס מ"ממתין" ל"שולם" - וזהו!</p>
          </div>
        ),
        placement: 'left',
        hideNextButton: false,
        nextLabel: 'המשך',
        blockInteraction: true,
        scrollToTarget: true,
        optional: true,
      },
    ],
  },
  // Phase 23: Finance - Invoice Creation
  {
    path: '/dashboard/finance',
    pathMatch: 'exact',
    steps: [
      {
        target: '.finance-header-actions',
        icon: Receipt,
        title: 'שלב 24: חשבוניות וקבלות',
        content: (
          <div>
            <p><strong>הפקת מסמכים פיננסיים בלחיצה!</strong></p>
            <ul className="onboarding-list">
              <li><Receipt size={14} weight="regular" className="list-icon" /> הפקת חשבונית מס / קבלה</li>
              <li><Envelope size={14} weight="regular" className="list-icon" /> שליחה אוטומטית במייל</li>
              <li><FloppyDisk size={14} weight="regular" className="list-icon" /> שמירה בתיק המטופל</li>
            </ul>
            <p className="onboarding-fun">בלי אקסלים, בלי כאב ראש!</p>
          </div>
        ),
        placement: 'bottom',
        hideNextButton: false,
        nextLabel: 'המשך',
        blockInteraction: true,
        scrollToTarget: true,
      },
    ],
  },
  // Phase 24: Finance - Summary
  {
    path: '/dashboard/finance',
    pathMatch: 'exact',
    steps: [
      {
        target: '.finance-page',
        icon: Sparkle,
        title: 'שלב 25: סיכום פיננסים',
        content: (
          <div>
            <p><strong>מעולה! סיימת את הסקירה!</strong></p>
            <p>עכשיו אתה יודע איך לנהל את הכספים בקליניקה שלך.</p>
            <p className="onboarding-hint">זכור: כל תשלום שמתקבל מתעדכן אוטומטית בסטטיסטיקות!</p>
          </div>
        ),
        placement: 'right',
        hideNextButton: false,
        nextLabel: 'סיום ההדרכה',
        navigateTo: '/dashboard',
      },
    ],
  },
  // Phase 25: Final - Quick Actions FAB
  {
    path: '/dashboard',
    pathMatch: 'exact',
    steps: [
      {
        target: '.quick-actions-fab',
        icon: Lightning,
        title: 'שלב 26: הכפתור הקסום!',
        content: (
          <div>
            <p>הכפתור הירוק הזה הוא החבר הכי טוב שלך!</p>
            <p>ממנו אפשר לעשות הכל בלחיצה אחת:</p>
            <ul className="onboarding-list">
              <li><UserPlus size={14} weight="regular" className="list-icon" /> <strong>מטופל חדש</strong> - הוספת מטופל מהירה</li>
              <li><CalendarCheck size={14} weight="regular" className="list-icon" /> <strong>פגישה חדשה</strong> - קביעת תור</li>
              <li><Receipt size={14} weight="regular" className="list-icon" /> <strong>חשבונית</strong> - הפקה מהירה</li>
            </ul>
            <p className="onboarding-complete">
              <Sparkle size={16} weight="fill" className="action-icon" />
              <strong>סיימת את ההדרכה!</strong> אתה מוכן להתחיל לעבוד.
            </p>
          </div>
        ),
        placement: 'top',
        hideNextButton: false, // Show "Finish" button
        isLastStep: true,
      },
    ],
  },
];

function OnboardingTour() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if mobile - don't show onboarding on mobile devices
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Redux selectors
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const isNutritionClinic = useSelector(selectIsNutritionClinic);

  // The flow this clinic actually walks. Therapy clinics get ONBOARDING_FLOW
  // untouched. Nutrition clinics drop every `nutritionHidden` phase — and when
  // a dropped phase carried the navigateTo into the next page, the step
  // before it inherits that navigateTo so the tour still moves on.
  // Every count/index below derives from this array, never from the constant.
  const tourFlow = useMemo(() => {
    if (!isNutritionClinic) return ONBOARDING_FLOW;
    return ONBOARDING_FLOW.reduce((acc, phase, i) => {
      if (phase.nutritionHidden) {
        const dropped = phase.steps[phase.steps.length - 1];
        const prev = acc[acc.length - 1];
        if (dropped?.navigateTo && prev && !prev.steps[prev.steps.length - 1].navigateTo) {
          const prevSteps = prev.steps.slice();
          prevSteps[prevSteps.length - 1] = { ...prevSteps[prevSteps.length - 1], navigateTo: dropped.navigateTo };
          acc[acc.length - 1] = { ...prev, steps: prevSteps };
        }
        return acc;
      }
      acc.push(phase);
      return acc;
    }, []);
  }, [isNutritionClinic]);

  // Local state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasCheckedInitial, setHasCheckedInitial] = useState(false);
  const [waitingForNavigation, setWaitingForNavigation] = useState(false);
  const [shouldShowWelcome, setShouldShowWelcome] = useState(false);
  const [targetCheckTrigger, setTargetCheckTrigger] = useState(0); // Used to re-check targets
  
  // Fetch data to check if user needs onboarding
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, therapistId, status: 'active', pageSize: 1 },
    { skip: !clinicId || !therapistId }
  );
  
  const todayStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString();
  }, []);
  
  const todayEnd = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today.toISOString();
  }, []);
  
  const { data: appointmentsData } = useGetAppointmentsQuery(
    { clinicId, therapistId, startDate: todayStart, endDate: todayEnd },
    { skip: !clinicId || !therapistId }
  );
  
  // Mutation to update therapist profile (for saving onboarding status to DB)
  const [updateTherapist] = useUpdateTherapistMutation();
  
  // Check if user is new and needs onboarding
  const hasPatients = (patientsData?.patients?.length || 0) > 0;
  const hasAppointments = (appointmentsData?.length || 0) > 0;
  const isNewUser = !hasPatients && !hasAppointments;
  
  // Check if onboarding was already completed (from DB)
  const onboardingCompletedInDB = therapistProfile?.onboardingCompleted === true;
  
  // Calculate total steps across all phases
  const totalSteps = useMemo(() => {
    return tourFlow.reduce((total, phase) => total + phase.steps.length, 0);
  }, [tourFlow]);
  
  // Calculate current step number (for progress)
  const currentStepNumber = useMemo(() => {
    let count = 0;
    for (let i = 0; i < currentPhaseIndex; i++) {
      count += tourFlow[i].steps.length;
    }
    return count + currentStepIndex;
  }, [currentPhaseIndex, currentStepIndex, tourFlow]);
  
  // Get current phase and step
  const currentPhase = tourFlow[currentPhaseIndex];
  const currentStep = currentPhase?.steps[currentStepIndex];
  
  // Check if current path matches the phase
  const isPathMatch = useMemo(() => {
    if (!currentPhase) return false;
    const pathname = location.pathname;
    if (currentPhase.pathMatch === 'exact') {
      return pathname === currentPhase.path || pathname === currentPhase.path + '/';
    }
    return pathname.startsWith(currentPhase.path);
  }, [currentPhase, location.pathname]);
  
  // Check initial onboarding status
  useEffect(() => {
    if (!therapistId || hasCheckedInitial) return;
    
    const savedStatus = getSavedOnboardingStatus(therapistId);
    const forceOnboarding = isOnboardingForced(therapistId);
    
    // If force onboarding is set (user clicked "הפעל הדרכה"), show it regardless of DB status
    if (forceOnboarding) {
      clearForceOnboarding(therapistId);
      setHasCheckedInitial(true);
      setShouldShowWelcome(true);
      return;
    }
    
    // If onboarding was already completed in DB, don't show it automatically
    if (onboardingCompletedInDB) {
      setHasCheckedInitial(true);
      return;
    }
    
    // For automatic onboarding (new users), check if user is new
    const shouldShowOnboarding = !savedStatus?.completed && !savedStatus?.skipped && isNewUser;
    
    if (shouldShowOnboarding) {
      // Check if there's a saved phase (resume)
      const savedPhase = getSavedPhase(therapistId);
      if (savedPhase.phaseIndex > 0 || savedPhase.stepIndex > 0) {
        // Resume from saved position
        setCurrentPhaseIndex(savedPhase.phaseIndex);
        setCurrentStepIndex(savedPhase.stepIndex);
        setRunTour(true);
        dispatch(startOnboarding()); // Update Redux state
      } else {
        setShouldShowWelcome(true);
      }
    }
    
    setHasCheckedInitial(true);
  }, [therapistId, isNewUser, hasCheckedInitial, onboardingCompletedInDB]);
  
  // Show welcome modal with delay
  useEffect(() => {
    if (!shouldShowWelcome) return;
    
    const timer = setTimeout(() => {
      setShowWelcomeModal(true);
      setShouldShowWelcome(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [shouldShowWelcome]);
  
  // Handle path changes - continue tour when user navigates (even by clicking links)
  useEffect(() => {
    if (!runTour) return;
    
    const currentStep = currentPhase?.steps[currentStepIndex];
    
    // Skip if waiting for special actions
    if (currentStep?.waitForAction) return;
    
    // If current step has navigateTo and user navigated there, move to next phase
    if (currentStep?.navigateTo) {
      const pathname = location.pathname;
      const targetPath = currentStep.navigateTo;
      
      // For exact paths like '/dashboard', only match exactly (not subpaths)
      // For other paths, also allow subpaths
      const isExactPath = targetPath === '/dashboard';
      const matches = isExactPath 
        ? (pathname === targetPath || pathname === targetPath + '/')
        : (pathname === targetPath || pathname === targetPath + '/' || pathname.startsWith(targetPath + '/'));
      
      if (matches) {
        // User navigated to the target! Show transition then move to next phase
        setWaitingForNavigation(true);
        
        // Smooth transition delay
        setTimeout(() => {
          const nextPhaseIndex = currentPhaseIndex + 1;
          if (nextPhaseIndex < tourFlow.length) {
            const nextPhase = tourFlow[nextPhaseIndex];
            
            // Check if next phase path matches current location
            const nextPathMatches = nextPhase.pathMatch === 'exact'
              ? (pathname === nextPhase.path || pathname === nextPhase.path + '/')
              : pathname.startsWith(nextPhase.path);
            
            if (nextPathMatches) {
              setCurrentPhaseIndex(nextPhaseIndex);
              setCurrentStepIndex(0);
              savePhase(therapistId, nextPhaseIndex, 0);
              
              // Delay before showing tooltip to let page render
              setTimeout(() => {
                setWaitingForNavigation(false);
              }, 600);
            }
          }
        }, 400);
      }
    }
    
    // Also handle waitingForNavigation state for programmatic navigation
    if (waitingForNavigation && !currentStep?.navigateTo) {
      const expectedPhase = tourFlow[currentPhaseIndex];
      if (expectedPhase) {
        const pathname = location.pathname;
        const matches = expectedPhase.pathMatch === 'exact' 
          ? (pathname === expectedPhase.path || pathname === expectedPhase.path + '/')
          : pathname.startsWith(expectedPhase.path);
        
        if (matches) {
          setTimeout(() => {
            setWaitingForNavigation(false);
            setCurrentStepIndex(0);
          }, 600);
        }
      }
    }
  }, [location.pathname, runTour, waitingForNavigation, currentPhaseIndex, currentPhase, currentStepIndex, therapistId, tourFlow]);
  
  // Listen for patient creation - when URL changes from /patients/new to /patients/[id]
  useEffect(() => {
    if (!runTour) return;
    
    const step = currentPhase?.steps[currentStepIndex];
    if (step?.waitForAction !== 'patient_created') return;
    
    // Check if we navigated to a patient profile page (patient was created).
    // A nutrition clinic's wizard finishes on מרכז הליווי instead.
    const patientProfileMatch = location.pathname.match(/\/dashboard\/(?:patients|nutrition)\/([^/]+)$/);
    if (patientProfileMatch && patientProfileMatch[1] !== 'new' && patientProfileMatch[1] !== 'create') {
      // Patient was created! Show transition and then move to next phase
      setWaitingForNavigation(true);
      
      // Delay before moving to next phase (smooth transition)
      setTimeout(() => {
        const nextPhaseIndex = currentPhaseIndex + 1;
        if (nextPhaseIndex < tourFlow.length) {
          setCurrentPhaseIndex(nextPhaseIndex);
          setCurrentStepIndex(0);
          savePhase(therapistId, nextPhaseIndex, 0);
          
          // Navigate to dashboard for the calendar step
          navigate('/dashboard');
          
          // Another delay to let page load before showing tooltip
          setTimeout(() => {
            setWaitingForNavigation(false);
          }, 800);
        }
      }, 1000);
    }
  }, [location.pathname, runTour, currentPhase, currentStepIndex, currentPhaseIndex, therapistId, navigate, tourFlow]);
  
  // Listen for appointment modal opening
  useEffect(() => {
    if (!runTour) return;
    
    const step = currentPhase?.steps[currentStepIndex];
    if (step?.waitForAction !== 'appointment_modal_opened') return;
    
    // Check if appointment modal is open
    const checkForModal = () => {
      const modal = document.querySelector('.appointment-modal');
      if (modal) {
        // Modal opened! Move to next phase immediately (no loading overlay - modal is visible)
        setTimeout(() => {
          const nextPhaseIndex = currentPhaseIndex + 1;
          if (nextPhaseIndex < tourFlow.length) {
            setCurrentPhaseIndex(nextPhaseIndex);
            setCurrentStepIndex(0);
            savePhase(therapistId, nextPhaseIndex, 0);
          }
        }, 400);
        return true;
      }
      return false;
    };
    
    // Check immediately
    if (checkForModal()) return;
    
    // Set up observer to watch for modal
    const observer = new MutationObserver(() => {
      if (checkForModal()) {
        observer.disconnect();
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [runTour, currentPhase, currentStepIndex, currentPhaseIndex, therapistId, tourFlow]);
  
  // Listen for appointment creation - when modal closes (appointment created)
  useEffect(() => {
    if (!runTour) return;
    
    const step = currentPhase?.steps[currentStepIndex];
    if (step?.waitForAction !== 'appointment_created') return;
    
    // Watch for modal closing (appointment created)
    const checkForModalClose = () => {
      const modal = document.querySelector('.appointment-modal');
      if (!modal) {
        // Modal closed! Move to next phase (calendar with ongoing indicator)
        setTimeout(() => {
          // Navigate to calendar to show ongoing appointment indicator
          if (location.pathname !== '/dashboard/calendar') {
            navigate('/dashboard/calendar');
          }
          
          setTimeout(() => {
            const nextPhaseIndex = currentPhaseIndex + 1;
            if (nextPhaseIndex < tourFlow.length) {
              setCurrentPhaseIndex(nextPhaseIndex);
              setCurrentStepIndex(0);
              savePhase(therapistId, nextPhaseIndex, 0);
            }
          }, 500);
        }, 300);
        return true;
      }
      return false;
    };
    
    // Set up observer to watch for modal closing
    const observer = new MutationObserver(() => {
      if (checkForModalClose()) {
        observer.disconnect();
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [location.pathname, runTour, currentPhase, currentStepIndex, currentPhaseIndex, therapistId, navigate, tourFlow]);
  
  // Listen for live session opened - when clicking ongoing appointment indicator
  useEffect(() => {
    if (!runTour) return;
    
    const step = currentPhase?.steps[currentStepIndex];
    if (step?.waitForAction !== 'live_session_opened') return;
    
    // Check if we navigated to live session page
    const liveSessionMatch = location.pathname.match(/\/dashboard\/live-session\/([^/]+)$/);
    if (liveSessionMatch) {
      setWaitingForNavigation(true);
      
      setTimeout(() => {
        const nextPhaseIndex = currentPhaseIndex + 1;
        if (nextPhaseIndex < tourFlow.length) {
          setCurrentPhaseIndex(nextPhaseIndex);
          setCurrentStepIndex(0);
          savePhase(therapistId, nextPhaseIndex, 0);
          
          setTimeout(() => {
            setWaitingForNavigation(false);
          }, 800);
        }
      }, 500);
    }
  }, [location.pathname, runTour, currentPhase, currentStepIndex, currentPhaseIndex, therapistId, tourFlow]);
  
  // Listen for end session modal opening
  useEffect(() => {
    if (!runTour) return;
    
    const step = currentPhase?.steps[currentStepIndex];
    if (step?.waitForAction !== 'end_session_modal_opened') return;
    
    // Check if end session modal is open
    const checkForModal = () => {
      const modal = document.querySelector('.end-session-modal');
      if (modal) {
        // Modal opened! Move to next phase
        setTimeout(() => {
          const nextPhaseIndex = currentPhaseIndex + 1;
          if (nextPhaseIndex < tourFlow.length) {
            setCurrentPhaseIndex(nextPhaseIndex);
            setCurrentStepIndex(0);
            savePhase(therapistId, nextPhaseIndex, 0);
          }
        }, 300);
        return true;
      }
      return false;
    };
    
    // Check immediately
    if (checkForModal()) return;
    
    // Set up observer to watch for modal
    const observer = new MutationObserver(() => {
      if (checkForModal()) {
        observer.disconnect();
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [runTour, currentPhase, currentStepIndex, currentPhaseIndex, therapistId, tourFlow]);
  
  // Listen for session end - when navigating from live-session back to dashboard
  useEffect(() => {
    if (!runTour) return;
    
    const step = currentPhase?.steps[currentStepIndex];
    
    // Handle performAction: click_end_session - wait for navigation to dashboard
    if (step?.performAction === 'click_end_session') {
      const isDashboard = location.pathname === '/dashboard' || location.pathname === '/dashboard/';
      if (isDashboard) {
        // Session ended and we're on dashboard!
        setWaitingForNavigation(true);
        
        setTimeout(() => {
          const nextPhaseIndex = currentPhaseIndex + 1;
          if (nextPhaseIndex < tourFlow.length) {
            setCurrentPhaseIndex(nextPhaseIndex);
            setCurrentStepIndex(0);
            savePhase(therapistId, nextPhaseIndex, 0);
            
            setTimeout(() => {
              setWaitingForNavigation(false);
            }, 800);
          }
        }, 500);
      }
      return;
    }
    
    // Handle waitForAction: session_ended
    if (step?.waitForAction !== 'session_ended') return;
    
    // Add click listener to confirm button to show loader immediately
    const confirmBtn = document.querySelector('.end-session-modal .confirm-btn');
    if (confirmBtn) {
      const handleConfirmClick = () => {
        setWaitingForNavigation(true);
      };
      confirmBtn.addEventListener('click', handleConfirmClick);
      
      // Cleanup
      return () => {
        confirmBtn.removeEventListener('click', handleConfirmClick);
      };
    }
  }, [runTour, currentPhase, currentStepIndex, tourFlow]);
  
  // Separate useEffect to detect navigation to dashboard after session end
  useEffect(() => {
    if (!runTour) return;
    
    const step = currentPhase?.steps[currentStepIndex];
    if (step?.waitForAction !== 'session_ended') return;
    
    // Check if we navigated back to dashboard (session ended)
    const isDashboard = location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    if (isDashboard) {
      // Session ended!
      setWaitingForNavigation(true);
      
      setTimeout(() => {
        const nextPhaseIndex = currentPhaseIndex + 1;
        if (nextPhaseIndex < tourFlow.length) {
          setCurrentPhaseIndex(nextPhaseIndex);
          setCurrentStepIndex(0);
          savePhase(therapistId, nextPhaseIndex, 0);
          
          setTimeout(() => {
            setWaitingForNavigation(false);
          }, 800);
        }
      }, 500);
    }
  }, [location.pathname, runTour, currentPhase, currentStepIndex, currentPhaseIndex, therapistId, tourFlow]);
  
  
  // Filter out optional steps where target doesn't exist
  const activeSteps = useMemo(() => {
    if (!currentPhase) {
      return [];
    }
    const filtered = currentPhase.steps.filter(step => {
      if (step.optional) {
        const element = document.querySelector(step.target);
        const exists = !!element;
        return exists;
      }
      return true;
    });
    return filtered;
  }, [currentPhase, location.pathname, currentPhaseIndex, targetCheckTrigger]);
  
  // Re-check targets after navigation (DOM might not be ready immediately)
  useEffect(() => {
    if (!runTour || waitingForNavigation) return;
    if (activeSteps.length === 0 && currentPhase?.steps.length > 0) {
      const timer = setTimeout(() => {
        setTargetCheckTrigger(prev => prev + 1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [runTour, waitingForNavigation, activeSteps.length, currentPhase]);
  
  // Handle navigation
  const handleNext = useCallback(() => {
    const step = activeSteps[currentStepIndex];

    // If this step has a performAction, execute it first
    if (step?.performAction === 'click_end_session') {
      // Find and click the confirm button in end session modal
      const confirmBtn = document.querySelector('.end-session-modal .confirm-btn, .end-session-modal button.primary, .end-session-content button:first-of-type');
      if (confirmBtn) {
        confirmBtn.click();
      }
      // The session_ended listener will handle the phase transition
      return;
    }
    
    // Handle tab click actions in Toolbox
    const tabActions = {
      'click_reminders_tab': 'תזכורות',
      'click_leads_tab': 'מתעניינים',
      'click_forms_tab': 'טפסים',
      'click_questionnaires_tab': 'שאלונים',
      'click_templates_tab': 'תבניות',
    };
    
    if (step?.performAction && tabActions[step.performAction]) {
      const tabName = tabActions[step.performAction];

      // Find and click the correct tab
      const tabs = document.querySelectorAll('.main-tab');
      let clicked = false;
      tabs.forEach(tab => {
        if (tab.textContent.includes(tabName)) {
          tab.click();
          clicked = true;
        }
      });
      
      if (clicked) {
        // Move to next phase after a short delay for tab content to render
        setTimeout(() => {
          const nextPhaseIndex = currentPhaseIndex + 1;
          if (nextPhaseIndex < tourFlow.length) {
            setCurrentPhaseIndex(nextPhaseIndex);
            setCurrentStepIndex(0);
            savePhase(therapistId, nextPhaseIndex, 0);
            // Trigger re-check of targets after DOM updates
            setTimeout(() => {
              setTargetCheckTrigger(prev => prev + 1);
            }, 300);
          }
        }, 200);
      }
      return;
    }
    
    // If this step has a navigation target
    if (step?.navigateTo) {
      // Move to next phase
      const nextPhaseIndex = currentPhaseIndex + 1;
      if (nextPhaseIndex < tourFlow.length) {
        setCurrentPhaseIndex(nextPhaseIndex);
        setCurrentStepIndex(0);
        setWaitingForNavigation(true);
        savePhase(therapistId, nextPhaseIndex, 0);
        navigate(step.navigateTo);
        
        // Reset waitingForNavigation after navigation completes
        setTimeout(() => {
          setWaitingForNavigation(false);
        }, 800);
      }
      return;
    }
    
    // Move to next step in current phase
    if (currentStepIndex < activeSteps.length - 1) {
      const nextStepIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextStepIndex);
      savePhase(therapistId, currentPhaseIndex, nextStepIndex);
    } else {
      // Move to next phase
      const nextPhaseIndex = currentPhaseIndex + 1;

      if (nextPhaseIndex < tourFlow.length) {
        const nextPhase = tourFlow[nextPhaseIndex];
        
        // Check if we need to navigate
        const currentPath = location.pathname;
        const needsNavigation = nextPhase.pathMatch === 'exact'
          ? (currentPath !== nextPhase.path && currentPath !== nextPhase.path + '/')
          : !currentPath.startsWith(nextPhase.path);

        if (needsNavigation) {
          setCurrentPhaseIndex(nextPhaseIndex);
          setCurrentStepIndex(0);
          setWaitingForNavigation(true);
          savePhase(therapistId, nextPhaseIndex, 0);
          navigate(nextPhase.path);
          
          // Reset waitingForNavigation after navigation completes
          setTimeout(() => {
            setWaitingForNavigation(false);
          }, 800);
        } else {
          setCurrentPhaseIndex(nextPhaseIndex);
          setCurrentStepIndex(0);
          savePhase(therapistId, nextPhaseIndex, 0);
        }
      } else {
        // Tour complete
        setRunTour(false);
        setShowCompleteModal(true);
        dispatch(completeOnboarding());
        saveOnboardingStatus(therapistId, { completed: true });
        localStorage.removeItem(`${ONBOARDING_PHASE_KEY}_${therapistId}`);
        // Save to DB
        if (therapistId) {
          updateTherapist({ uid: therapistId, data: { onboardingCompleted: true } });
        }
      }
    }
  }, [currentStepIndex, currentPhaseIndex, activeSteps, therapistId, dispatch, navigate, location.pathname, updateTherapist, tourFlow]);
  
  const handlePrev = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevStepIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevStepIndex);
      savePhase(therapistId, currentPhaseIndex, prevStepIndex);
    } else if (currentPhaseIndex > 0) {
      // Go to previous phase (last step)
      const prevPhaseIndex = currentPhaseIndex - 1;
      const prevPhase = tourFlow[prevPhaseIndex];
      const prevStepIndex = prevPhase.steps.length - 1;
      
      // Navigate if needed
      const currentPath = location.pathname;
      const needsNavigation = prevPhase.pathMatch === 'exact'
        ? (currentPath !== prevPhase.path && currentPath !== prevPhase.path + '/')
        : !currentPath.startsWith(prevPhase.path);
      
      if (needsNavigation) {
        navigate(prevPhase.path);
      }
      
      setCurrentPhaseIndex(prevPhaseIndex);
      setCurrentStepIndex(prevStepIndex);
      savePhase(therapistId, prevPhaseIndex, prevStepIndex);
    }
  }, [currentStepIndex, currentPhaseIndex, therapistId, navigate, location.pathname, tourFlow]);
  
  const handleSkip = useCallback(() => {
    setRunTour(false);
    dispatch(skipOnboarding());
    saveOnboardingStatus(therapistId, { skipped: true });
    localStorage.removeItem(`${ONBOARDING_PHASE_KEY}_${therapistId}`);
    // Save to DB
    if (therapistId) {
      updateTherapist({ uid: therapistId, data: { onboardingCompleted: true } });
    }
  }, [dispatch, therapistId, updateTherapist]);
  
  // Handle welcome modal actions
  const handleStartTour = useCallback(() => {
    setShowWelcomeModal(false);
    setRunTour(true);
    dispatch(startOnboarding()); // Update Redux state
    setCurrentPhaseIndex(0);
    setCurrentStepIndex(0);
    savePhase(therapistId, 0, 0);
    
    // Navigate to dashboard if not there
    if (location.pathname !== '/dashboard' && location.pathname !== '/dashboard/') {
      navigate('/dashboard');
    }
  }, [therapistId, location.pathname, navigate]);
  
  const handleSkipTour = useCallback(() => {
    setShowWelcomeModal(false);
    dispatch(skipOnboarding());
    saveOnboardingStatus(therapistId, { skipped: true });
    // Save to DB
    if (therapistId) {
      updateTherapist({ uid: therapistId, data: { onboardingCompleted: true } });
    }
  }, [dispatch, therapistId, updateTherapist]);
  
  // Handle complete modal close
  const handleCompleteClose = useCallback(() => {
    setShowCompleteModal(false);
  }, []);
  
  // Handle replay tour
  const handleReplayTour = useCallback(() => {
    setShowCompleteModal(false);
    setCurrentPhaseIndex(0);
    setCurrentStepIndex(0);
    setRunTour(true);
    dispatch(startOnboarding()); // Update Redux state
    savePhase(therapistId, 0, 0);
    
    // Navigate to dashboard if not there
    if (location.pathname !== '/dashboard' && location.pathname !== '/dashboard/') {
      navigate('/dashboard');
    }
  }, [therapistId, location.pathname, navigate]);
  
  // Get the actual step to render (considering active steps filtering)
  const stepToRender = activeSteps[currentStepIndex];
  
  // Don't render spotlight if waiting for navigation or no active steps
  const shouldShowSpotlight = runTour && !waitingForNavigation && isPathMatch && stepToRender;
  
  // Debug logging for render decision
  useEffect(() => {
    if (runTour) {
    }
  }, [runTour, waitingForNavigation, isPathMatch, location.pathname, currentPhase, stepToRender, activeSteps.length, shouldShowSpotlight]);
  
  // Don't render onboarding on mobile devices
  if (isMobile) {
    return null;
  }
  
  return (
    <>
      {/* Welcome Modal */}
      <OnboardingWelcomeModal
        isOpen={showWelcomeModal}
        onStart={handleStartTour}
        onSkip={handleSkipTour}
        therapistName={therapistProfile?.firstName}
      />
      
      {/* Complete Modal */}
      <OnboardingCompleteModal
        isOpen={showCompleteModal}
        onClose={handleCompleteClose}
        therapistName={therapistProfile?.firstName}
      />
      
      {/* Tour Spotlight */}
      {shouldShowSpotlight && (
        <Spotlight
          target={stepToRender.target}
          placement={stepToRender.placement}
          onNext={handleNext}
          onPrev={handlePrev}
          onSkip={handleSkip}
          stepIndex={currentStepNumber}
          totalSteps={totalSteps}
          isLastStep={currentPhaseIndex === tourFlow.length - 1 && currentStepIndex === activeSteps.length - 1}
          isActionStep={stepToRender.isActionStep}
          nextLabel={stepToRender.nextLabel}
          hideNextButton={stepToRender.hideNextButton}
          blockInteraction={stepToRender.blockInteraction}
          scrollToTarget={stepToRender.scrollToTarget}
        >
          {stepToRender.title && (
            <h3 className="onboarding-title">
              {stepToRender.icon && <stepToRender.icon size={22} weight="duotone" className="title-icon" />}
              {/* Titles hardcode "שלב N:" for the full flow; a shortened flow
                  renumbers them from the live position so N never skips. */}
              {tourFlow === ONBOARDING_FLOW
                ? stepToRender.title
                : stepToRender.title.replace(/^שלב \d+:/, `שלב ${currentStepNumber + 1}:`)}
            </h3>
          )}
          <div className="onboarding-text">
            {typeof stepToRender.content === 'function'
              ? stepToRender.content({ isNutritionClinic })
              : stepToRender.content}
          </div>
        </Spotlight>
      )}
      
      {/* Loading state while navigating between steps */}
      {runTour && waitingForNavigation && (
        <div className="onboarding-loading-overlay">
          <div className="onboarding-loading-content">
            <LoaderLoop size="lg" />
            <p>מעולה! ממשיכים...</p>
            <span className="onboarding-loading-hint">רגע קטן...</span>
          </div>
        </div>
      )}
    </>
  );
}

export default OnboardingTour;
